import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { RedemptionCalendar } from '@/components/shared/RedemptionCalendar';

const STATUS_BADGE = 'text-xs font-semibold px-2 py-0.5 rounded-full bg-bone text-charcoal';
const TZ = 'America/Edmonton';

function paidBy(p) {
  if (p.staff?.name) return `Recorded by ${p.staff.name}`;
  if (p.source === 'ONLINE') return 'Paid online by customer';
  return 'Not yet recorded';
}

// Read-only, shared full customer profile — used by the admin customer page AND the POS
// "View Full Profile". Admin passes onAddNote + linkSubscriptions to enable extra affordances.
export function CustomerProfileView({
  customer,
  redemptions = [],
  payments = [],
  linkSubscriptions = false,
  noteInput = '',
  onNoteInputChange,
  onAddNote,
  savingNote = false,
}) {
  const activePlan = customer.subscriptions.find((s) => s.status === 'ACTIVE')?.plan?.name || '—';

  return (
    <div className="space-y-4" data-testid="customer-profile-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-border/40 p-4 text-sm">
          <p className="text-muted-ink text-xs mb-1">Registered</p>
          <p className="text-charcoal font-medium">{new Date(customer.createdAt).toLocaleDateString('en-CA')}</p>
        </div>
        <div className="bg-white rounded-xl border border-border/40 p-4 text-sm">
          <p className="text-muted-ink text-xs mb-1">Account Status</p>
          <span className={STATUS_BADGE}>{customer.status}</span>
        </div>
        <div className="bg-white rounded-xl border border-border/40 p-4 text-sm">
          <p className="text-muted-ink text-xs mb-1">Subscriptions</p>
          <p className="text-charcoal font-medium">{customer.subscriptions.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-border/40 p-4 text-sm">
          <p className="text-muted-ink text-xs mb-1">Current Plan</p>
          <p className="text-charcoal font-medium">{activePlan}</p>
        </div>
      </div>

      <Tabs defaultValue="subscriptions">
        <TabsList data-testid="customer-profile-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="subscriptions" data-testid="tab-profile-subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-profile-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="redemptions" data-testid="tab-profile-redemptions">Redemptions</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-profile-payments">Payments</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-profile-notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="subscriptions" className="space-y-2 mt-4">
          {customer.subscriptions.map((s) => {
            const inner = (
              <>
                <div className="flex justify-between">
                  <p className="font-medium text-charcoal">{s.plan?.name} — {s.subscriptionCode}</p>
                  <span className={STATUS_BADGE}>{s.status}</span>
                </div>
                <p className="text-xs text-muted-ink mt-1">
                  {s.remainingCredits}/{s.originalCredits} remaining · Expires {new Date(s.expiryDate).toLocaleDateString('en-CA')}
                  {s.bonusCredits > 0 && <span className="text-coriander"> · +{s.bonusCredits} bonus</span>}
                </p>
              </>
            );
            return linkSubscriptions ? (
              <a
                key={s.id}
                href={`/admin/subscriptions/${s.id}`}
                data-testid="profile-subscription-row"
                className="block bg-white rounded-xl border border-border/40 p-4 hover:border-saffron/40 transition-colors duration-150"
              >
                {inner}
              </a>
            ) : (
              <div key={s.id} data-testid="profile-subscription-row" className="bg-white rounded-xl border border-border/40 p-4">
                {inner}
              </div>
            );
          })}
          {customer.subscriptions.length === 0 && <p className="text-sm text-muted-ink">No subscriptions yet.</p>}
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <RedemptionCalendar redemptions={redemptions} showStaff />
        </TabsContent>

        <TabsContent value="redemptions" className="mt-4">
          <div className="bg-white rounded-xl border border-border/40 divide-y divide-border/30">
            {redemptions.map((r) => (
              <div key={r.id} className="p-3 flex flex-wrap gap-x-3 gap-y-1 text-sm" data-testid="profile-redemption-row">
                <span>{new Date(r.createdAt).toLocaleString('en-CA', { timeZone: TZ })}</span>
                <span>{r.balanceBefore} → {r.balanceAfter}</span>
                <span className="text-muted-ink">{r.staff?.name}</span>
              </div>
            ))}
            {redemptions.length === 0 && <p className="p-4 text-sm text-muted-ink">No redemptions yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="bg-white rounded-xl border border-border/40 divide-y divide-border/30">
            {payments.map((p) => (
              <div key={p.id} className="p-3 text-sm" data-testid="profile-payment-row">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-charcoal">${Number(p.amount).toFixed(2)} CAD</span>
                  <span className={STATUS_BADGE}>{p.status}</span>
                </div>
                <p className="text-xs text-muted-ink mt-1">
                  {new Date(p.paymentDate).toLocaleString('en-CA', { timeZone: TZ })} ·{' '}
                  {p.method} · {p.source === 'ONLINE' ? 'Online' : 'In-Store'} ·{' '}
                  {p.purpose === 'TOPUP' ? 'Top-up' : 'New subscription'}
                </p>
                <p className="text-xs text-coriander mt-0.5" data-testid="profile-payment-recorded-by">{paidBy(p)}</p>
                {p.referenceNumber && <p className="text-xs text-muted-ink mt-0.5">Ref: {p.referenceNumber}</p>}
              </div>
            ))}
            {payments.length === 0 && <p className="p-4 text-sm text-muted-ink">No payments recorded yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-4 space-y-3">
          {onAddNote && (
            <div className="flex gap-2">
              <Textarea
                data-testid="add-note-textarea"
                value={noteInput}
                onChange={(e) => onNoteInputChange?.(e.target.value)}
                placeholder="Add an internal note…"
              />
              <Button data-testid="add-note-btn" disabled={savingNote} onClick={onAddNote} className="bg-saffron hover:bg-saffron-hover shrink-0">
                Add
              </Button>
            </div>
          )}
          <div className="space-y-2">
            {(customer.notes || []).map((n) => (
              <div key={n.id} className="bg-white rounded-xl border border-border/40 p-3 text-sm" data-testid="customer-note-row">
                <p className="text-charcoal">{n.note}</p>
                <p className="text-xs text-muted-ink mt-1">{n.staff?.name} · {new Date(n.createdAt).toLocaleString('en-CA', { timeZone: TZ })}</p>
              </div>
            ))}
            {(customer.notes || []).length === 0 && <p className="text-sm text-muted-ink">No notes yet.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
